import React, { useState, useCallback } from "react";
import { Post, PostStatus } from "@/core/types/postTypes";
import { useAppStore } from "@/core/store/useAppStore";
import { SchedulePostModal } from "./SchedulePostModal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

interface ScheduleButtonProps {
  post: Post;
  compact?: boolean;
  variant?: "default" | "grid";
  className?: string;
  onScheduled?: () => void;
}

const CalendarIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const compactStyles = {
  default: "p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed",
  grid: "p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed",
};

export const ScheduleButton: React.FC<ScheduleButtonProps> = ({
  post,
  compact = false,
  variant = "default",
  className = "",
  onScheduled,
}) => {
  const { selectedClient } = useAppStore();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showNotReadyDialog, setShowNotReadyDialog] = useState(false);

  const canSchedule =
    post.status === PostStatus.READY ||
    post.status === PostStatus.SCHEDULED;

  const handleScheduleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (canSchedule) {
        setShowScheduleModal(true);
      } else {
        setShowNotReadyDialog(true);
      }
    },
    [canSchedule]
  );

  const handleCloseModal = useCallback(() => {
    setShowScheduleModal(false);
  }, []);

  const handleScheduled = useCallback(() => {
    setShowScheduleModal(false);
    onScheduled?.();
  }, [onScheduled]);

  const button = compact ? (
    <button
      onClick={handleScheduleClick}
      disabled={!selectedClient}
      className={`${compactStyles[variant]} ${className}`}
      title={post.status === PostStatus.SCHEDULED ? 'Reschedule' : 'Schedule for later'}
    >
      <CalendarIcon />
    </button>
  ) : (
    <button
      onClick={handleScheduleClick}
      disabled={!selectedClient}
      className={`w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${className}`}
    >
      <CalendarIcon />
      {post.status === PostStatus.SCHEDULED ? 'Reschedule' : 'Schedule for Later'}
    </button>
  );

  return (
    <>
      {button}

      {showScheduleModal && selectedClient && (
        <SchedulePostModal
          post={post}
          clientId={selectedClient.id}
          onClose={handleCloseModal}
          onScheduled={handleScheduled}
        />
      )}

      <ConfirmDialog
        isOpen={showNotReadyDialog}
        onClose={() => setShowNotReadyDialog(false)}
        onConfirm={() => setShowNotReadyDialog(false)}
        title="Post Not Ready"
        message={'This post must be in "Ready" status before it can be scheduled. Update the post status to Ready first.'}
        confirmText="OK"
        variant="info"
      />
    </>
  );
};
